import { HyperFormula, type DetailedCellError } from 'hyperformula';
import type { Column, Rec } from '../api';
import { RU_TO_EN } from '../formula-catalog';

/**
 * Настройки движка восстановлены из бандла: считаем на английских именах,
 * разделитель аргументов «;», десятичный «,», разряды — пробел.
 */
const OPTIONS = {
  licenseKey: 'gpl-v3',
  language: 'enGB',
  useColumnIndex: true,
  functionArgSeparator: ';',
  decimalSeparator: '.' as const,
  thousandSeparator: ' ' as const,
  dateFormats: ['DD.MM.YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY.MM.DD', 'MM/DD/YYYY'],
};

/**
 * Русский языковой пакет не подключаем: путь к нему разный от версии к
 * версии и ломает сборку. Он был нужен только ради русских кодов ошибок,
 * а имена функций мы и так переводим сами через RU_TO_EN.
 */
/** Расшифровки для подсказки при наведении — словарь из оригинала. */
export const ERROR_HINTS: Record<string, string> = {
  '#NAME?': 'Неизвестная функция или имя. Проверьте написание.',
  '#REF!': 'Ссылка указывает на несуществующую ячейку.',
  '#VALUE!': 'Неверный тип аргумента.',
  '#DIV/0!': 'Деление на ноль.',
  '#N/A': 'Значение не найдено (например VLOOKUP не нашёл ключ).',
  '#NUM!': 'Некорректное число (например корень из отрицательного).',
  '#NULL!': 'Пересечение диапазонов пусто.',
  '#ERROR!': 'Синтаксическая ошибка формулы.',
  '#CYCLE!': 'Циклическая ссылка.',
  '#SPILL!': 'Результат не помещается — соседние ячейки заняты.',
};

const ERROR_RU: Record<string, string> = {
  '#DIV/0!': '#ДЕЛ/0!',
  '#N/A': '#Н/Д',
  '#NAME?': '#ИМЯ?',
  '#NULL!': '#ПУСТО!',
  '#NUM!': '#ЧИСЛО!',
  '#REF!': '#ССЫЛКА!',
  '#VALUE!': '#ЗНАЧ!',
  '#CYCLE!': '#ЦИКЛ!',
  '#ERROR!': '#ОШИБКА!',
  '#SPILL!': '#ПЕРЕНОС!',
};

/** Функции, значение которых меняется само по себе — их нельзя кэшировать. */
const VOLATILE = /\b(TODAY|NOW|RAND|RANDBETWEEN|СЕГОДНЯ|ТДАТА|СЛЧИС|СЛУЧМЕЖДУ)\s*\(/i;

export const isVolatile = (formula: string) => VOLATILE.test(formula);

/** «СУММ(A1:A9)» → «SUM(A1:A9)». Трогаем только имена перед скобкой. */
export function translateToEnglish(formula: string): string {
  if (!formula.startsWith('=')) return formula;
  const translated = formula.replace(
    /([A-Za-zА-Яа-яЁё_][A-Za-zА-Яа-яЁё0-9_.]*)(\s*\()/g,
    (_m, name: string, tail: string) => (RU_TO_EN.get(name.toUpperCase()) ?? name) + tail,
  );

  // Проверяем, содержит ли формула точку с запятой ';' вне строковых литералов
  let inQuotes = false;
  let hasSemicolons = false;
  for (let i = 0; i < translated.length; i++) {
    const ch = translated[i];
    if (ch === '"') inQuotes = !inQuotes;
    if (!inQuotes && ch === ';') {
      hasSemicolons = true;
      break;
    }
  }

  // Нормализуем формулу под HyperFormula (functionArgSeparator: ';', decimalSeparator: '.'):
  // 1) Если формула в английском стиле (без ';'): запятые вне строк являются разделителями аргументов -> преобразуем в ';'
  // 2) Если формула в русском стиле (с ';'): запятая между цифрами '0,5' является десятичной -> преобразуем в '.'
  inQuotes = false;
  let res = '';
  for (let i = 0; i < translated.length; i++) {
    const ch = translated[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      res += ch;
      continue;
    }
    if (!inQuotes) {
      if (hasSemicolons) {
        if (
          ch === ',' &&
          i > 0 &&
          i < translated.length - 1 &&
          /\d/.test(translated[i - 1]) &&
          /\d/.test(translated[i + 1])
        ) {
          res += '.';
        } else {
          res += ch;
        }
      } else {
        if (ch === ',') {
          res += ';';
        } else {
          res += ch;
        }
      }
    } else {
      res += ch;
    }
  }
  return res;
}

/** Преобразует буквы колонки ('A', 'Z', 'AA') в 0-based индекс (0, 25, 26). */
export function colLettersToIndex(letters: string): number {
  let col = 0;
  const upper = letters.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    col = col * 26 + (upper.charCodeAt(i) - 64);
  }
  return col - 1;
}

/** Преобразует 0-based индекс (0, 25, 26) в буквы колонки ('A', 'Z', 'AA'). */
export function indexToColLetters(index: number): string {
  let temp = index + 1;
  let letter = '';
  while (temp > 0) {
    const mod = (temp - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    temp = Math.floor((temp - mod) / 26);
  }
  return letter;
}

function transformUnquoted(segment: string, dr: number, dc: number): string {
  return segment.replace(
    /(^|[^A-Za-z0-9_])(\$?)([A-Za-z]{1,3})(\$?)([1-9][0-9]*)(?!\s*\()/g,
    (_match, pre: string, colDollar: string, colLetters: string, rowDollar: string, rowDigits: string) => {
      let newCol = colLetters;
      if (!colDollar && dc !== 0) {
        const colIdx = colLettersToIndex(colLetters);
        const targetColIdx = Math.max(0, colIdx + dc);
        newCol = indexToColLetters(targetColIdx);
        if (colLetters === colLetters.toLowerCase()) {
          newCol = newCol.toLowerCase();
        }
      }

      let newRow = rowDigits;
      if (!rowDollar && dr !== 0) {
        const rowNum = parseInt(rowDigits, 10);
        const targetRowNum = Math.max(1, rowNum + dr);
        newRow = String(targetRowNum);
      }

      return `${pre}${colDollar}${newCol}${rowDollar}${newRow}`;
    },
  );
}

/**
 * Смещает относительные ссылки в формуле на dr строк и dc колонок (как при автозаполнении в Excel).
 * Абсолютные ссылки ($A$1, $A1, A$1) сохраняют заблокированную часть.
 * Строковые литералы внутри кавычек ("...") не затрагиваются.
 */
export function shiftFormulaReferences(formula: string, dr: number, dc: number = 0): string {
  if (!formula || !formula.trimStart().startsWith('=')) return formula;
  if (dr === 0 && dc === 0) return formula;

  const trimmed = formula.trimStart();
  const prefix = formula.slice(0, formula.length - trimmed.length);

  let result = '';
  let inQuotes = false;
  let currentSegment = '';

  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed[i];
    if (char === '"') {
      if (inQuotes) {
        if (i + 1 < trimmed.length && trimmed[i + 1] === '"') {
          currentSegment += '""';
          i++;
          continue;
        }
        inQuotes = false;
        result += '"' + currentSegment + '"';
        currentSegment = '';
      } else {
        result += transformUnquoted(currentSegment, dr, dc);
        currentSegment = '';
        inQuotes = true;
      }
    } else {
      currentSegment += char;
    }
  }

  if (inQuotes) {
    result += '"' + currentSegment;
  } else {
    result += transformUnquoted(currentSegment, dr, dc);
  }

  return prefix + result;
}

/** Excel хранит даты числом от 30.12.1899 — эта же база была в оригинале. */
const EPOCH = Date.UTC(1899, 11, 30);
export const serialToDate = (serial: number) => new Date(EPOCH + serial * 864e5);

export type CellType = string;

/** Ровно то, что HyperFormula принимает в ячейке. */
type RawCell = string | number | boolean | null;

export interface Computed {
  value: unknown;
  type: CellType;
  isError: boolean;
  formatted: string;
  /** Английский код ошибки, если ячейка сломалась: #REF!, #DIV/0!… */
  errorCode?: string;
}

export function formatValue(value: unknown, type: CellType, colType?: string): string {
  if (value === null || value === undefined) return '';

  if (typeof value === 'object' && value !== null && 'value' in value) {
    // DetailedCellError — код приходит английским, показываем русский.
    const code = String((value as DetailedCellError).value);
    return ERROR_RU[code] ?? code;
  }

  if (typeof value === 'number') {
    if (type === 'NUMBER_DATE') return serialToDate(value).toLocaleDateString('ru');
    if (type === 'NUMBER_DATETIME') return serialToDate(value).toLocaleString('ru');
    if (type === 'NUMBER_PERCENT') return `${(value * 100).toFixed(2)} %`;
    if (colType === 'Int' || type === 'NUMBER_INTEGER' || Number.isInteger(value) || Math.abs(value - Math.round(value)) < 1e-6) {
      return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(value));
    }
    return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);
  }

  if (typeof value === 'boolean') return value ? 'ИСТИНА' : 'ЛОЖЬ';
  return String(value);
}

const isErrorValue = (v: unknown): v is DetailedCellError =>
  typeof v === 'object' && v !== null && 'type' in v && 'value' in v;

/** Формула — это значение ячейки, начинающееся со знака равенства. */
export const isFormula = (value: unknown): value is string =>
  typeof value === 'string' && value.trimStart().startsWith('=');

export interface SheetContext {
  id: string;
  name: string;
  columns: Column[];
  records: Rec[];
}

function sheetToGrid(columns: Column[], records: Rec[]): RawCell[][] {
  return records.map((rec) =>
    columns.map((col) => {
      const raw = rec.fields[col.fields.colId];

      if (isFormula(raw)) return translateToEnglish(raw.trim());
      if (raw === undefined || raw === null) return null;
      if (typeof raw === 'number' || typeof raw === 'boolean') return raw;
      if (typeof raw === 'string') {
        const s = raw.trim();
        if (s === '') return null;
        if (/^-?\d+(\.\d+)?$/.test(s)) {
          const num = Number(s);
          if (!isNaN(num)) return num;
        }
        const m = s.match(/^\$?\s*([0-9]{1,3}(?:,[0-9]{3})+(?:\.[0-9]+)?)\s*\$?$/);
        if (m) {
          const num = parseFloat(m[1].replace(/,/g, ''));
          if (!isNaN(num)) return num;
        }
        return s;
      }
      return String(raw);
    }),
  );
}

/**
 * Считает лист целиком: формулой становится любая ячейка, чей текст
 * начинается с «=». Колонка при этом остаётся обычной — формулы
 * принадлежат ячейкам, как в Excel.
 * При передаче allSheets поддерживает межстраничные формулы ('Лист'!A1).
 */
export function computeSheet(
  columns: Column[],
  records: Rec[],
  allSheets?: SheetContext[],
  activeSheetName?: string,
): Map<string, Computed> {
  const isMultiSheet = Boolean(allSheets && allSheets.length > 1);

  let engine: HyperFormula;
  let activeSheetId = 0;

  if (isMultiSheet && allSheets) {
    engine = HyperFormula.buildEmpty(OPTIONS);
    for (const sheet of allSheets) {
      engine.addSheet(sheet.name);
      const sId = engine.getSheetId(sheet.name) ?? 0;
      const grid = sheetToGrid(sheet.columns, sheet.records);
      if (grid.length > 0 && grid[0].length > 0) {
        engine.setCellContents({ sheet: sId, col: 0, row: 0 }, grid);
      }
      if (activeSheetName && sheet.name === activeSheetName) {
        activeSheetId = sId;
      }
    }
  } else {
    const grid = sheetToGrid(columns, records);
    engine = HyperFormula.buildFromArray(grid, OPTIONS);
  }

  const result = new Map<string, Computed>();

  try {
    records.forEach((rec, row) => {
      columns.forEach((col, c) => {
        if (!isFormula(rec.fields[col.fields.colId])) return;

        let value = engine.getCellValue({ sheet: activeSheetId, row, col: c });
        const type = engine.getCellValueDetailedType({ sheet: activeSheetId, row, col: c }) as CellType;
        const failed = isErrorValue(value);

        const isIntCol = col.fields.type === 'Int' || /преми/i.test(col.fields.label || '');
        if (typeof value === 'number' && isIntCol) {
          value = Math.round(value);
        }

        result.set(`${rec.id}:${col.fields.colId}`, {
          value,
          type,
          isError: failed,
          formatted: formatValue(value, isIntCol ? 'NUMBER_INTEGER' : type, col.fields.type),
          errorCode: failed ? String((value as DetailedCellError).value) : undefined,
        });
      });
    });
  } finally {
    engine.destroy();
  }

  return result;
}
